import React from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import ButtonText from 'shared/components/vxl/typography/component/ButtonText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export type VXLPromoCardColorTheme = 'white' | 'grey';

export interface VXLPromoCardProps extends PressableAnalyticsProps {
  bodyText: string;
  actionText: string;
  illustration?: IllustrationSourceType;
  onPress: () => void;
  colorTheme?: VXLPromoCardColorTheme;
  onDismiss?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  dismissAccessibilityLabel?: string;
  testID?: string;
}

const BODY_MAX_LINES = 3;
const ACTION_TEXT_MAX_LINES = 1;
const DISMISS_ICON_SIZE = 24;
const ILLUSTRATION_SIZE = 48;

const VXLPromoCard = ({
  bodyText,
  actionText,
  illustration,
  onPress,
  colorTheme = 'white',
  onDismiss,
  dismissAccessibilityLabel = 'Dismiss card',
  disabled,
  accessibilityLabel,
  testID = '',
  analyticsId = undefined,
  customAttributes = null,
}: VXLPromoCardProps) => {
  const themeStyles = colorThemeStyles[colorTheme];

  return (
    <PressableWithDelay
      style={({ hovered, focused, pressed }) => [
        styles.container,
        themeStyles.background,
        hovered && themeStyles.hovered,
        // Only show focus ring for keyboard navigation, not pointer clicks. TODO: Replace with focusVisible once available in a future RN upgrade.
        focused && !hovered && !pressed && themeStyles.focused,
        pressed && themeStyles.pressed,
      ]}
      // TODO: Remove when focusVisible is available in a future RN upgrade.
      onPressOut={(e) => {
        if (isWeb) {
          e.currentTarget.blur();
        }
      }}
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      analyticsId={analyticsId}
      customAttributes={customAttributes}
      role="button"
      accessible
      accessibilityLabel={accessibilityLabel ?? actionText}
    >
      <View style={styles.contentContainer}>
        {illustration ? (
          <View style={styles.illustrationContainer}>
            <Illustration illustration={illustration} />
          </View>
        ) : null}
        <View style={styles.textContainer}>
          <BodySmText
            numberOfLines={BODY_MAX_LINES}
            style={disabled && styles.disabledText}
          >
            {bodyText}
          </BodySmText>
          <ButtonText
            numberOfLines={ACTION_TEXT_MAX_LINES}
            style={disabled && styles.disabledText}
          >
            {actionText}
          </ButtonText>
        </View>
      </View>
      {onDismiss ? (
        <PressableWithDelay
          style={({ hovered, focused, pressed }) => [
            dismissButtonStyles.button,
            hovered && dismissButtonStyles.hovered,
            // Only show focus ring for keyboard navigation, not pointer clicks. TODO: Replace with focusVisible once available in a future RN upgrade.
            focused && !hovered && !pressed && dismissButtonStyles.focused,
            pressed && dismissButtonStyles.pressed,
          ]}
          // TODO: Remove when focusVisible is available in a future RN upgrade.
          onPressOut={(e) => {
            if (isWeb) {
              e.currentTarget.blur();
            }
          }}
          onPress={onDismiss}
          disabled={disabled}
          accessibilityLabel={dismissAccessibilityLabel}
          accessible
          role="button"
        >
          <Icon
            iconKey="CLOSE"
            iconSize="SM"
            iconColor={
              disabled
                ? theme.color.interactive.disabled.text
                : theme.color.text.primary
            }
          />
        </PressableWithDelay>
      ) : null}
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  contentContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  illustrationContainer: {
    height: ILLUSTRATION_SIZE,
    width: ILLUSTRATION_SIZE,
  },
  textContainer: {
    flex: 1,
    gap: theme.spacing.s,
  },
});

const dismissButtonStyles = StyleSheet.create({
  button: {
    alignItems: 'center',
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: DISMISS_ICON_SIZE / 2,
    height: DISMISS_ICON_SIZE,
    justifyContent: 'center',
    marginRight: theme.spacing.s,
    marginTop: theme.spacing.s,
    width: DISMISS_ICON_SIZE,
  },
  focused: {
    outlineColor: theme.color.interactive.focused.border,
    outlineStyle: 'solid',
    outlineWidth: 1,
  },
  hovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  pressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
});

interface ColorThemeStyle {
  background: ViewStyle;
  hovered: ViewStyle;
  pressed: ViewStyle;
  focused: ViewStyle;
}

const colorThemeStyles: Record<VXLPromoCardColorTheme, ColorThemeStyle> = {
  white: StyleSheet.create({
    background: {
      backgroundColor: theme.color.interactive.secondary,
    },
    focused: {
      outlineColor: theme.color.interactive.focused.border,
      outlineStyle: 'solid',
      outlineWidth: 1,
    },
    hovered: {
      backgroundColor: theme.color.interactive.secondaryHovered,
    },
    pressed: {
      backgroundColor: theme.color.interactive.secondaryPressed,
    },
  }),
  grey: StyleSheet.create({
    /* eslint-disable react-native/no-unused-styles */
    background: {
      backgroundColor: theme.color.interactive.tertiary,
    },
    focused: {
      outlineColor: theme.color.interactive.focused.border,
      outlineStyle: 'solid',
      outlineWidth: 1,
    },
    hovered: {
      backgroundColor: theme.color.interactive.tertiaryHovered,
    },
    pressed: {
      backgroundColor: theme.color.interactive.tertiaryPressed,
    },
  }),
};

export default VXLPromoCard;
